import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Upload, Download, Trash2, FileText, Eye, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { BulkFileUpload } from './BulkFileUpload';
import { UnifiedFilePreview } from '@/components/shared/UnifiedFilePreview';
import { DragDropZone } from './DragDropZone';
import { Label } from '@/components/ui/label';
import { logStorageAccessError } from '@/lib/securityLogger';
import { normalizeStoragePath } from '@/lib/storageUtils';

interface LocationBusinesscaseFilesProps {
  locationId: string;
}

export function LocationBusinesscaseFiles({ locationId }: LocationBusinesscaseFilesProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState<any | null>(null);

  const { data: files = [], isLoading, isError } = useQuery({
    queryKey: ['location-businesscase-files', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('location_businesscase_files')
        .select(`
          *,
          profiles:uploaded_by(display_name, email)
        `)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    retry: 1,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadProgress(10);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${locationId}/${crypto.randomUUID()}.${fileExt}`;
      
      setUploadProgress(30);
      
      const { error: uploadError } = await supabase.storage
        .from('location-businesscases')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        await logStorageAccessError({
          operation: 'write',
          bucket: 'location-businesscases',
          path: fileName,
          errorMessage: uploadError.message,
          userId: user?.id,
        });
        throw new Error(`Storage fout: ${uploadError.message}`);
      }
      
      setUploadProgress(70);

      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('location-businesscases')
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);

      if (signedUrlError) {
        await supabase.storage.from('location-businesscases').remove([fileName]);
        throw new Error(`Signed URL fout: ${signedUrlError.message}`);
      }

      const signedUrl = signedUrlData.signedUrl;
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      setUploadProgress(85);

      const { error: dbError } = await supabase
        .from('location_businesscase_files')
        .insert({
          location_id: locationId,
          file_name: file.name,
          file_url: signedUrl,
          storage_path: fileName,
          url_expires_at: expiresAt,
          file_size: file.size,
          uploaded_by: user?.id,
        });

      if (dbError) {
        await supabase.storage.from('location-businesscases').remove([fileName]);
        throw new Error(`Database fout: ${dbError.message}`);
      }
      
      setUploadProgress(100);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-businesscase-files'] });
      toast({ title: 'Bestand geüpload' });
      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij uploaden', 
        description: error.message,
        variant: 'destructive' 
      });
      setIsUploading(false);
      setUploadProgress(0);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await supabase
        .from('location_businesscase_files')
        .delete()
        .eq('id', fileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-businesscase-files'] });
      toast({ title: 'Bestand verwijderd' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij verwijderen', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({ 
        title: 'Bestand te groot', 
        description: 'Maximale bestandsgrootte is 10MB',
        variant: 'destructive' 
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast({ title: 'Geen bestand geselecteerd', variant: 'destructive' });
      return;
    }
    setIsUploading(true);
    uploadMutation.mutate(selectedFile);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale: nl });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Businesscase Bestanden
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bulk Upload Section */}
        <div className="border rounded-lg p-4 bg-muted/30">
          <h4 className="text-sm font-medium mb-3">Meerdere bestanden uploaden</h4>
          <BulkFileUpload
            locationId={locationId}
            bucket="location-businesscases"
            tableName="location_businesscase_files"
          />
        </div>

        {/* Single Upload Section */}
        <div className="border rounded-lg p-4 bg-muted/30">
          <h4 className="text-sm font-medium mb-3">Bestand uploaden</h4>
          <div className="space-y-3">
            <div>
              <Label className="text-sm mb-2 block">Bestand (max 10MB)</Label>
              <DragDropZone
                onFilesSelected={(files) => {
                  const file = files[0];
                  if (!file) return;

                  if (file.size > 10 * 1024 * 1024) {
                    toast({
                      title: 'Bestand te groot',
                      description: 'Maximale bestandsgrootte is 10MB',
                      variant: 'destructive',
                    });
                    return;
                  }

                  setSelectedFile(file);
                }}
                accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                maxFiles={1}
                maxSize={10}
              />
            </div>
            
            {selectedFile && !isUploading && (
              <>
                <p className="text-sm text-muted-foreground">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
                <Button 
                  onClick={handleUpload} 
                  disabled={!selectedFile || isUploading}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload bestand
                </Button>
              </>
            )}
            
            {isUploading && uploadProgress > 0 && (
              <div className="space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">
                  {uploadProgress < 30 ? 'Voorbereiden...' :
                   uploadProgress < 70 ? 'Uploaden naar storage...' :
                   uploadProgress < 90 ? 'Opslaan in database...' :
                   'Afronden...'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Files List */}
        {isLoading ? (
          <p className="text-center text-muted-foreground py-4">Laden...</p>
        ) : isError ? (
          <p className="text-center text-destructive py-4">Fout bij laden van bestanden</p>
        ) : files.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Geen bestanden geüpload</p>
        ) : (
          <div className="space-y-2">
            {files.map((file: any) => (
              <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{file.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.file_size)} • {formatDate(file.created_at)}
                      {file.profiles?.display_name && ` • ${file.profiles.display_name}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewFile({ 
                      file_name: file.file_name, 
                      file_size: file.file_size,
                      bucket: 'location-businesscases',
                      storage_path: file.storage_path
                    })}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data, error } = await supabase.storage
                          .from('location-businesscases')
                          .download(file.storage_path);
                        
                        if (error) throw error;
                        
                        const blobUrl = URL.createObjectURL(data);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = file.file_name || 'download';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(blobUrl);
                        
                        toast({ title: 'Download gestart' });
                      } catch (error: any) {
                        await logStorageAccessError({
                          operation: 'read',
                          bucket: 'location-businesscases',
                          path: file.storage_path,
                          errorMessage: error.message,
                          userId: user?.id,
                        });
                        toast({
                          title: 'Fout bij downloaden',
                          description: error.message,
                          variant: 'destructive',
                        });
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm('Weet je zeker dat je dit bestand wilt verwijderen?')) {
                        deleteMutation.mutate(file.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview Modal */}
        {previewFile && (
          <UnifiedFilePreview
            file={{
              name: previewFile.file_name,
              bucket: previewFile.bucket,
              path: previewFile.storage_path,
              size: previewFile.file_size,
            }}
            open={!!previewFile}
            onOpenChange={(open) => !open && setPreviewFile(null)}
            portal="charging"
          />
        )}
      </CardContent>
    </Card>
  );
}
